import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../../contexts/AuthContext';
import { colors, breakpoints } from '../../../assets/styles/GlobalStyles';
import { FaSignOutAlt, FaUser, FaQuestionCircle, FaFileAlt, FaBell, FaTimes } from 'react-icons/fa';
import UserInfo from '../../../modules/user/components/UserInfo';
import { SupportCard } from '../../../modules/support/components/SupportCard';

const MenuContainer = styled.div`
  position: relative;
  display: inline-block;
  min-width: 0;
  max-width: 100%;
`;

// min-width: 0 deixa o nome do usuário encolher (ellipsis) em vez de espremer o título do Header
const MenuButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  min-width: 300px;
  background: ${colors.white};
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  overflow: hidden;
  animation: slideDown 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0, 0, 0, 0.08);

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: ${breakpoints.mobile}) {
    min-width: 280px;
    right: -10px;
    border-radius: 18px;
  }

  // Drawer em coluna: cabeçalho fixo e lista rolável ocupando o resto (o "Sair" nunca fica escondido)
  @media (max-width: ${breakpoints.small}) {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: auto;
    width: 85%;
    max-width: 320px;
    height: 100vh;
    height: 100dvh;
    display: flex;
    flex-direction: column;
    border-radius: 0;
    border-radius: 24px 0 0 24px;
    animation: slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: -8px 0 32px rgba(0, 0, 0, 0.15);
    transform: translateX(0);
    
    @keyframes slideInRight {
      from {
        transform: translateX(100%);
      }
      to {
        transform: translateX(0);
      }
    }
  }
`;

const DropdownMenuClosing = styled(DropdownMenu)`
  @media (max-width: ${breakpoints.small}) {
    animation: slideOutRight 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    
    @keyframes slideOutRight {
      from {
        transform: translateX(0);
      }
      to {
        transform: translateX(100%);
      }
    }
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  overscroll-behavior: contain;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (min-width: 481px) {
    display: none;
  }
`;

const MobileUserHeader = styled.div`
  display: none;

  @media (max-width: ${breakpoints.small}) {
    display: flex;
    flex-shrink: 0;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 24px 20px;
    border-bottom: 1px solid ${colors.borderLight};
    background: linear-gradient(135deg, ${colors.backgroundLight}, ${colors.white});
  }
`;

const MobileCloseButton = styled.button`
  background: rgba(0, 0, 0, 0.05);
  border: none;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 18px;
  color: #666;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MenuItemsContainer = styled.div`
  padding: 8px 0;
  max-height: 70vh;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.borderLight};
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.lightBlue};
    border-radius: 4px;
  }

  @media (max-width: ${breakpoints.small}) {
    flex: 1;
    min-height: 0;
    max-height: none;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 16px 0;
  }
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  gap: 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
  text-align: left;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  &:hover {
    background: ${colors.backgroundLight};
    padding-left: 28px;
  }

  svg {
    color: ${colors.darkBlue};
    font-size: 18px;
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: scale(1.1);
  }

  @media (max-width: ${breakpoints.small}) {
    padding: 16px 24px;
    font-size: 15px;
    
    &:hover {
      padding-left: 28px;
    }
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: linear-gradient(to right, transparent, ${colors.borderLight}, transparent);
  margin: 8px 24px;
`;

const MenuItemLogout = styled(MenuItem)`
  color: ${colors.red};
  margin-top: 4px;
  margin-bottom: env(safe-area-inset-bottom);
  
  svg {
    color: ${colors.red};
  }

  &:hover {
    background: rgba(166, 3, 14, 0.05);
  }
`;

const Menu = ({ children, onMenuItemClick }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [isSupportCardOpen, setIsSupportCardOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const handleOpenSupport = () => {
    setIsSupportCardOpen(true);
  };

  const closeMenu = useCallback(() => {
    if (isMobile) {
      setIsClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
      }, 300);
    } else {
      setIsOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 480;
      setIsMobile(mobile);
      if (!mobile && isOpen) {
        closeMenu();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, closeMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isMobile, closeMenu]);

  // Trava a rolagem do #root (é ele que rola; body já é overflow hidden no index.html)
  useEffect(() => {
    if (!isOpen || !isMobile) return undefined;
    const root = document.getElementById('root');
    if (!root) return undefined;
    const overflowAnterior = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => {
      root.style.overflow = overflowAnterior;
    };
  }, [isOpen, isMobile]);

  const handleLogout = async () => {
    closeMenu();
    await logout();
    navigate('/login');
  };

  const toggleMenu = () => {
    if (isOpen) {
      closeMenu();
    } else {
      setIsOpen(true);
    }
  };

  const handleMenuItemClick = (callback) => {
    return () => {
      closeMenu();
      if (callback) callback();
      if (onMenuItemClick) onMenuItemClick();
    };
  };

  const defaultMenuItems = [
    { icon: FaUser, label: 'Meu Perfil', action: () => navigate('/perfil') },
    { icon: FaFileAlt, label: 'Minhas Solicitações', action: () => navigate('/solicitacoes') },
    { icon: FaBell, label: 'Notificações', action: () => navigate('/pendencias') },
    { icon: FaQuestionCircle, label: 'Ajuda e Suporte', action: handleOpenSupport },
  ];

  const renderMenuItems = () => {
    if (children) return children;

    return defaultMenuItems.map((item, index) => {
      const Icon = item.icon;
      return (
        <MenuItem key={index} onClick={handleMenuItemClick(item.action)}>
          <Icon /> {item.label}
        </MenuItem>
      );
    });
  };

  const MenuComponent = isClosing ? DropdownMenuClosing : DropdownMenu;

  return (
    <>
      <MenuContainer ref={menuRef}>
        <MenuButton onClick={toggleMenu} ref={buttonRef}>
          <UserInfo
            showDetails={true}
            isOpen={isOpen}
            onClick={toggleMenu}
            hideOnMobile={true}
          />
        </MenuButton>

        {isOpen && (
          <>
            {isMobile && <Overlay onClick={closeMenu} />}

            <MenuComponent>
              <MobileUserHeader>
                <UserInfo
                  showDetails={true}
                  hideOnMobile={false}
                  isInMenu={true}
                />
                <MobileCloseButton onClick={closeMenu}>
                  <FaTimes />
                </MobileCloseButton>
              </MobileUserHeader>

              <MenuItemsContainer>
                {renderMenuItems()}
                <MenuDivider />
                <MenuItemLogout onClick={handleMenuItemClick(handleLogout)}>
                  <FaSignOutAlt /> Sair do Sistema
                </MenuItemLogout>
              </MenuItemsContainer>
            </MenuComponent>
          </>
        )}
      </MenuContainer>

      <SupportCard 
        isOpen={isSupportCardOpen} 
        onClose={() => setIsSupportCardOpen(false)} 
      />
    </>
  );
};

export default Menu;